import {AfterViewInit, Component, ElementRef, HostListener, inject, OnInit, ViewChild} from '@angular/core';
import {DatePipe, NgClass} from "@angular/common";
import {NgSelectModule} from "@ng-select/ng-select";
import {EventHttpService} from "../../../management/x-http-requests/event-http.service";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {EventService} from "../_services/event.service";
import {FormsModule} from "@angular/forms";
import {EventDisplayComponent} from "../_components/event-display/event-display.component";
import {EmployeeEvent} from "../../../models/event/employee-event";
import {Tuple} from "../../../models/_generic/tuple";
import {ResizeEvent} from "leaflet";
import {EmployeeEventResponseSummary} from "../../company/_models/employee/employee-event-response-summary";
import {IcsService} from "../_services/ics.service";
import {SmallCalendarComponent} from "../../x-components/small-calendar/small-calendar.component";
import {ShiftHttpService} from "../_services/shift-http.service";
import {CalendarDay} from "../../x-components/small-calendar/x-models/calendar-day";
import {CompanyTimeZoneService} from "../../../_shared/time/company-time-zone.service";
import {companyDayKey, companyToday, DayKey, dayKeyToPickerDate} from "../../../_shared/time/company-day";

@Component({
  selector: 'app-calendar',
  imports: [
    DatePipe,
    NgSelectModule,
    FormsModule,
    EventDisplayComponent,
    NgClass,
    SmallCalendarComponent
  ],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss'
})
export class CalendarComponent implements OnInit, AfterViewInit {
  /** Today as a picker date for the header; set to the COMPANY day once the zone is known. */
  today: Date = new Date();
  /** Today as a company calendar day; the month picker renders once it is known (see `ngOnInit`). */
  protected todayKey: DayKey | undefined;
  calendarVisible: boolean = false;
  @ViewChild('calendar') calendar: ElementRef | undefined;
  @ViewChild('input') input: ElementRef | undefined;
  @ViewChild('top') top: ElementRef | undefined;
  b: number = 0;
  i: number = 0;
  public backdrop: boolean = true;
  protected shifts: EmployeeEventResponseSummary[] = [];
  private readonly companyTimeZone = inject(CompanyTimeZoneService);

  constructor(private eventHttpService: EventHttpService, private gas: GlobalAlertService,
              public eventService: EventService,
              protected IcsService: IcsService,
              private shiftHttpService: ShiftHttpService) {
  }

  /** Active tab: my accepted shifts, all events, or open (unanswered) events. */
  private _tab: 'mine' | 'all' | 'open' = 'all';

  get tab() {
    return this._tab;
  }

  set tab(value: 'mine' | 'all' | 'open') {
    this._tab = value;
    this.eventService.filter();
  }

  /** Mobile only: the month picker is collapsed by default; tapping "Datum" reveals it for filtering. */
  protected calendarOpen = false;

  protected toggleCalendar() {
    this.calendarOpen = !this.calendarOpen;
  }

  private _search = "";

  get search() {
    return this._search;
  }

  set search(value: string) {
    this._search = value;
    this.eventService.filter();
  }

  get calenderBottom(): number {
    let rect = this.calendar?.nativeElement.getBoundingClientRect();
    if (rect == undefined) return 0
    return rect.height;
  }

  filterFunc = (e: EmployeeEvent) => {
    // Tab filters:
    //  - "mine"  → only events the employee was *assigned* to (eingeteilt = has a loaded shift for this event).
    //  - "open"  → no response sent yet AND the response window is still open (can still respond).
    //  - "all"   → everything.
    if (this._tab === 'mine' && this.shifts_getForEvent(e.id).length === 0) return false;
    if (this._tab === 'open' &&
      !(e.employeeEventResponse.active === undefined && e.publicResponseEnd > new Date())) return false;

    if (this._search != "" && !(e.publicTitle != "" ? e.publicTitle : e.name).toLowerCase().includes(this._search.toLowerCase()) &&
      !e.publicDescription.toLowerCase().includes(this._search.toLowerCase()) &&
      !e.shardName.toLowerCase().includes(this._search.toLowerCase())) return false;

    return true;
  }

  ngOnInit(): void {
    this.eventService.filterFunc = this.filterFunc;
    // "Today" and the initially selected day are the COMPANY's today (#656), so the zone comes first. The picker is
    // rendered only once `todayKey` is set, so it never builds (and selects in) the browser's month first.
    this.companyTimeZone.load().subscribe(timeZone => {
      this.todayKey = companyToday(timeZone);
      this.today = dayKeyToPickerDate(this.todayKey);
      this.eventService.selectedDay = new CalendarDay(this.today, false);
      // The forward feed, unbounded above: the list has no horizon, so this is the request that fills it. The
      // month picker's own emit follows during the first change-detection pass and tops it up with the visible
      // month's PAST days — a small, response-free prefix request. Loading here rather than leaving it all to
      // that emit keeps the screen working if the picker ever stops emitting on init.
      this.eventService.load();
      this.loadShifts();
    });
  }

  /**
   * The picker selected a day. The shift list is NOT reloaded: the overview endpoint is unbounded (#645) and was
   * loaded once in `ngOnInit`; the per-event matching in {@link shifts_getForEvent} is by event id, not by day.
   */
  protected onSelectedDateChange(day: CalendarDay) {
    this.eventService.filter();
    // Selecting a day moves the RESPONSES window with it (the list starts here, and only the list needs the
    // answers). It is passed as the events bound too so that picking a day earlier than anything loaded pulls
    // those events in as well — `load` widens only, so the common forward case issues no request.
    this.eventService.load(day?.date, day?.date);
  }

  /**
   * The month picker changed which days are on screen. Those days' dots are drawn from the loaded events, so
   * paging BACKWARDS has to pull that month in — the events window follows the visible range, not the
   * selected day. Paging forwards is already covered and issues no request.
   *
   * `map()` still runs on every change: the dots are rebuilt from what is already held, immediately, rather
   * than waiting for a round-trip that may not even be needed.
   */
  protected onDateRangeChange(range: Tuple<Date, Date>) {
    this.eventService.map();
    if (range?.item1) this.eventService.load(range.item1, this.eventService.selectedDay.date);
  }

  /**
   * Loads ALL of the employee's assigned shifts once (the endpoint takes no range, #645). They populate the per-event
   * `responses` (via {@link shifts_getForEvent}) that drive the minimal "assigned" event-card variant — it replaces
   * the former (hidden) "Nächste Dienste" widget, which only existed to feed this data.
   */
  private loadShifts() {
    this.shiftHttpService.getShiftOverview().subscribe({
      next: overview => {
        this.shifts = overview.employeeEventResponses;
        // re-run the filter so the "Meine" (assigned) tab reflects the shifts once they arrive
        this.eventService.filter();
      },
      error: err => {
        console.error(err);
        this.shifts = [];
      }
    });
  }

  getShards(events: EmployeeEvent[]): Tuple<string, string>[] {
    return events
      .filter((v, i, a) => this.distinct(v, (value: EmployeeEvent) => value.shardId, i, a))
      .map(x => new Tuple<string, string>(x.shardName, x.color));
  }

  distinct = (value: any, func: (any: any) => any, index: number, array: any[]) => {
    return array.findIndex(x => func(x) == func(value)) == index;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: ResizeEvent) {
    let rect = this.input?.nativeElement.getBoundingClientRect();
    if (rect) {
      this.i = rect.y;
    }

    rect = this.top?.nativeElement.getBoundingClientRect();
    if (rect) {
      this.i -= rect.y;
    }
  }

  ngAfterViewInit() {
    this.b = this.calenderBottom;
    let rect = this.input?.nativeElement.getBoundingClientRect();
    if (rect) {
      this.i = rect.y;
    }

    rect = this.top?.nativeElement.getBoundingClientRect();
    if (rect) {
      this.i -= rect.y;
    }


    setTimeout(() => {
      this.backdrop = false;
    }, 500)
  }

  protected shifts_getForEvent(event: string): EmployeeEventResponseSummary[] {
    return this.shifts.filter(x => x.eventId == event);
  }

  // ----- day-grouped list (modern layout) -----

  /**
   * Groups the filtered events by the COMPANY calendar day of their EVENT start, ascending (#656). Not the browser's
   * day: an event at 01:00 Berlin on 01.08. is listed under 01.08. for a viewer in New York too. `day` is a
   * browser-local date of that calendar day, for the `date` pipe only.
   *
   * This returns FRESH arrays on every call, so the template's `@for` blocks must track by a stable identity
   * (`group.key` / `event.id`). Without that, Angular tears down and rebuilds every event card — and
   * their `ngModel` comment textareas — on each change-detection cycle, which visibly freezes the page while a
   * card is being interacted with.
   */
  protected grouped(): { key: DayKey; day: Date; events: EmployeeEvent[] }[] {
    const timeZone = this.companyTimeZone.timeZone();
    const events = this.eventService.events_filtered ?? [];
    const map = new Map<DayKey, { key: DayKey; day: Date; events: EmployeeEvent[] }>();
    for (const e of [...events].sort((a, b) => a.start.getTime() - b.start.getTime())) {
      const key = companyDayKey(e.start, timeZone);
      if (!map.has(key)) map.set(key, {key, day: dayKeyToPickerDate(key), events: []});
      map.get(key)!.events.push(e);
    }
    return [...map.values()];
  }
}
